import { RRule, Weekday as RRuleWeekday } from 'rrule'

export type Frequency = 'WEEKLY' | 'MONTHLY_NTH' | 'MONTHLY_DAY'
export const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const
export type Weekday = (typeof WEEKDAYS)[number]

export interface RecurrenceState {
  frequency: Frequency
  weekdays: Weekday[] // WEEKLY
  monthlyWeekday: Weekday // MONTHLY_NTH
  monthlyPosition: number // MONTHLY_NTH: 1..4 or -1 (last)
  monthlyDayOfMonth: number // MONTHLY_DAY: 1..31
}

// Map our tokens to rrule weekday constants.
const RRULE_WEEKDAY: Record<Weekday, RRuleWeekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
}
// RRule.MO.weekday === 0
const TOKEN_BY_RRULE_INDEX: Weekday[] = [
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
  'SU',
]

export function buildRrule(state: RecurrenceState): string {
  let rule: RRule

  switch (state.frequency) {
    case 'WEEKLY':
      // NOTE: do NOT pass dtstart — keeps DTSTART out of the output.
      rule = new RRule({
        freq: RRule.WEEKLY,
        byweekday: state.weekdays.map((d) => RRULE_WEEKDAY[d]),
      })
      break
    case 'MONTHLY_NTH':
      // 3rd Tuesday → RRule.TU.nth(3) → BYDAY=+3TU ; last Fri → RRule.FR.nth(-1) → BYDAY=-1FR
      rule = new RRule({
        freq: RRule.MONTHLY,
        byweekday: [
          RRULE_WEEKDAY[state.monthlyWeekday].nth(state.monthlyPosition),
        ],
      })
      break
    case 'MONTHLY_DAY':
      rule = new RRule({
        freq: RRule.MONTHLY,
        bymonthday: [state.monthlyDayOfMonth],
      })
      break
  }

  return normalizeToBarePattern(rule, state.frequency === 'WEEKLY')
}

// Strip DTSTART line + RRULE: prefix + newlines; optionally re-inject WKST=MO for weekly.
function normalizeToBarePattern(rule: RRule, injectWkst: boolean): string {
  let pattern = rule
    .toString()
    .split('\n')
    .filter((line) => !line.startsWith('DTSTART'))
    .join('')
    .replace(/^RRULE:/, '')
    .trim()

  if (injectWkst && !/(^|;)WKST=/.test(pattern)) {
    // Place WKST=MO right after FREQ=WEEKLY to match the handoff example exactly.
    pattern = pattern.replace(/^FREQ=WEEKLY/, 'FREQ=WEEKLY;WKST=MO')
  }

  // Contract guard (dev): bare pattern only.
  if (/DTSTART|RRULE:|\n/.test(pattern)) {
    throw new Error(`RRULE is not a bare pattern: ${JSON.stringify(pattern)}`)
  }
  return pattern
}

export function parseRrule(pattern: string): RecurrenceState {
  const options = RRule.parseString(pattern) // accepts a bare 'FREQ=...' string

  // Default state
  const state: RecurrenceState = {
    frequency: 'WEEKLY',
    weekdays: [],
    monthlyWeekday: 'MO',
    monthlyPosition: 1,
    monthlyDayOfMonth: 1,
  }

  const byweekday = toArray(options.byweekday) // rrule may give Weekday | number | array
  if (options.freq === RRule.WEEKLY) {
    state.frequency = 'WEEKLY'
    state.weekdays = byweekday
      .map(weekdayToken)
      .filter((token): token is Weekday => token !== undefined)
  } else if (options.freq === RRule.MONTHLY && byweekday.length > 0) {
    state.frequency = 'MONTHLY_NTH'
    const wd = byweekday[0] as RRuleWeekday // has .weekday and .n
    state.monthlyWeekday = TOKEN_BY_RRULE_INDEX[wd.weekday]
    state.monthlyPosition = wd.n ?? 1
  } else if (options.freq === RRule.MONTHLY) {
    state.frequency = 'MONTHLY_DAY'
    state.monthlyDayOfMonth = toArray(options.bymonthday)[0] ?? 1
  }
  return state
}

export function describeRrule(pattern: string): string {
  return RRule.fromString(pattern).toText() // e.g. "every week on Tuesday"
}

// --- small helpers for parseRrule ----------------------------------------

// rrule options expose scalars, arrays, or null/undefined — normalize to an array.
function toArray<T>(value: T | T[] | null | undefined): T[] {
  if (value === null || value === undefined) return []
  return Array.isArray(value) ? value : [value]
}

// Convert an rrule weekday (Weekday instance, integer 0=MO..6=SU, or a token
// string like 'TU') to our token.
function weekdayToken(
  value: RRuleWeekday | number | string
): Weekday | undefined {
  if (typeof value === 'string') {
    const upper = value.toUpperCase()
    return WEEKDAYS.find((token) => token === upper)
  }
  const index = typeof value === 'number' ? value : value.weekday
  return TOKEN_BY_RRULE_INDEX[index]
}

// --- time helpers (UNCHANGED from FE-04 §3) ------------------------------
// Seconds, not milliseconds. rrule is not involved here.

// Combine a date input value ('YYYY-MM-DD') and time input value ('HH:mm')
// into an epoch timestamp in seconds, interpreted in the user's local zone.
export function toEpochSeconds(dateStr: string, timeStr: string): number {
  const [year, month, day] = dateStr.split('-').map(Number)
  const [hours, minutes] = timeStr.split(':').map(Number)
  const date = new Date(year, month - 1, day, hours, minutes, 0, 0)
  return Math.floor(date.getTime() / 1000)
}

// Inverse of toEpochSeconds: split an epoch timestamp (seconds) back into the
// date ('YYYY-MM-DD') and time ('HH:mm') input values in the local zone.
export function fromEpochSeconds(epochSeconds: number): {
  date: string
  time: string
} {
  const d = new Date(epochSeconds * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`
  return { date, time }
}
